import React from "react";
import {
  Box,
  Typography,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { formatErpMoney } from "../../utils/formatErpMoney";
import { formatErpDate } from "./ErpDialogShared";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 10
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5)
  },
  sectionTitle: {
    fontSize: "0.8rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: theme.palette.text.secondary
  },
  amounts: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: theme.spacing(1.5)
  },
  amountBox: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 8,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.05)"
        : "rgba(0,0,0,0.03)",
    border: `1px solid ${theme.palette.divider}`
  },
  amountLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: theme.palette.text.secondary,
    marginBottom: 4
  },
  amountValue: {
    fontSize: "1rem",
    fontWeight: 600
  },
  saldoValue: {
    color: theme.palette.error.main
  },
  pagadoValue: {
    color: theme.palette.success.dark
  },
  note: {
    marginTop: theme.spacing(1),
    fontSize: "0.8rem",
    color: theme.palette.text.secondary
  },
  progress: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
    borderRadius: 4,
    height: 6
  },
  paymentsPaper: {
    borderRadius: 10,
    overflow: "hidden",
    marginTop: theme.spacing(2)
  },
  chipPagada: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText
  },
  chipParcial: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText
  },
  chipPendiente: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText
  },
  chipNeutral: {
    backgroundColor: theme.palette.grey[500],
    color: "#fff"
  }
}));

const statusChipClass = (status, classes) => {
  if (status === "pagada") return classes.chipPagada;
  if (status === "parcial") return classes.chipParcial;
  if (status === "anulada" || status === "cancelada") return classes.chipNeutral;
  return classes.chipPendiente;
};

const ErpPaymentSection = ({ summary, linkedFacturaLabel }) => {
  const classes = useStyles();

  if (!summary) return null;

  const {
    symbol,
    total,
    totalAbonado,
    saldoPendiente,
    pagos,
    status,
    statusLabel,
    source
  } = summary;

  const progress =
    total > 0 ? Math.min(100, Math.round((totalAbonado / total) * 100)) : 0;

  return (
    <Paper variant="outlined" className={classes.card} elevation={0}>
      <Box className={classes.header}>
        <Typography className={classes.sectionTitle}>Estado de cobro</Typography>
        <Chip
          size="small"
          label={statusLabel}
          className={statusChipClass(status, classes)}
        />
      </Box>

      <Box className={classes.amounts}>
        <Box className={classes.amountBox}>
          <Typography className={classes.amountLabel}>Total</Typography>
          <Typography className={classes.amountValue}>
            {formatErpMoney(total, symbol)}
          </Typography>
        </Box>
        <Box className={classes.amountBox}>
          <Typography className={classes.amountLabel}>Pagado</Typography>
          <Typography className={`${classes.amountValue} ${classes.pagadoValue}`}>
            {formatErpMoney(totalAbonado, symbol)}
          </Typography>
        </Box>
        <Box className={classes.amountBox}>
          <Typography className={classes.amountLabel}>Saldo pendiente</Typography>
          <Typography className={`${classes.amountValue} ${classes.saldoValue}`}>
            {formatErpMoney(saldoPendiente, symbol)}
          </Typography>
        </Box>
      </Box>

      {total > 0 && status !== "anulada" && status !== "cancelada" && (
        <Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            className={classes.progress}
            color={status === "pagada" ? "primary" : "secondary"}
          />
          <Typography variant="caption" color="textSecondary">
            {progress}% abonado del total
          </Typography>
        </Box>
      )}

      {source === "factura" && linkedFacturaLabel && (
        <Typography className={classes.note}>
          Cobro según factura vinculada {linkedFacturaLabel}
        </Typography>
      )}

      {source === "documento" && summary.linkedFacturaId == null && (
        <Typography className={classes.note}>
          Orden sin factura vinculada: no hay saldo pendiente registrado en cuentas
          por cobrar.
        </Typography>
      )}

      {pagos && pagos.length > 0 && (
        <Paper variant="outlined" className={classes.paymentsPaper} elevation={0}>
          <Typography
            className={classes.sectionTitle}
            style={{ padding: "12px 16px 0" }}
          >
            Pagos registrados ({pagos.length})
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Forma de pago</TableCell>
                  <TableCell>Referencia</TableCell>
                  <TableCell align="right">Monto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagos.map((pago, idx) => {
                  const pagoSymbol =
                    pago.tenantCurrency?.symbol || symbol;
                  return (
                    <TableRow
                      key={
                        pago.reciboId ||
                        `${pago.fecha}-${pago.monto}-${idx}`
                      }
                    >
                      <TableCell>{formatErpDate(pago.fecha)}</TableCell>
                      <TableCell>
                        {pago.tipoPago?.nombre || "—"}
                        {pago.usaSaldoFavor && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                          >
                            Saldo a favor
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{pago.referencia || "—"}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {formatErpMoney(pago.monto, pagoSymbol)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {pagos && pagos.length === 0 && saldoPendiente > 0.009 && (
        <Typography className={classes.note}>
          Sin pagos registrados en el ERP.
        </Typography>
      )}
    </Paper>
  );
};

export default ErpPaymentSection;
